import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type MutationOptions,
  type QueryClient,
} from '@tanstack/react-query';
import {
  api,
  ErroDaApi,
  type AssinaturaDaApi,
  type PaginaDeAnexos,
  type BuscaSalvaDaApi,
  type Conta,
  type Contato,
  type ContatoSalvo,
  type ConversaDaApi,
  type CorpoDaApi,
  type MensagemDaApi,
  type PaginaDeContatos,
  type PaginaDeConversas,
  type Pasta,
  type Preferencias,
  type RegraDaApi,
  type RespostaProntaDaApi,
  type ResultadoDaImportacao,
} from './api';
import { tempoRealConectado } from './tempoReal';

/**
 * Consultas da aplicação. Ficam num arquivo só para as chaves de cache não se
 * espalharem: invalidar a coisa errada é o jeito mais rápido de a tela mentir.
 */

export const chaves = {
  contas: ['contas'] as const,
  pastas: (accountId: string) => ['pastas', accountId] as const,
  conversas: (filtros: FiltrosDeConversa) => ['conversas', filtros] as const,
  mensagem: (id: string) => ['mensagem', id] as const,
  corpo: (id: string, imagens: boolean) => ['corpo', id, imagens] as const,
  busca: (q: string, accountId: string | undefined) => ['busca', q, accountId ?? null] as const,
  contatos: (q: string) => ['contatos', q] as const,
  // Tudo do painel de hospedagem sob um prefixo: qualquer escrita invalida o painel inteiro.
  paineis: ['hospedagem'] as const,
  painel: (id: string) => ['hospedagem', id] as const,
  caixasDoPainel: (id: string, dominio: string) => ['hospedagem', id, 'caixas', dominio] as const,
  dominiosDoPainel: (id: string) => ['hospedagem', id, 'dominios'] as const,
  encaminhamentos: (id: string) => ['hospedagem', id, 'encaminhamentos'] as const,
  respostasAutomaticas: (id: string) => ['hospedagem', id, 'respostas'] as const,
  // `agenda` e nao `contatos`: invalidar a agenda nao pode derrubar o cache
  // da sugestao do composer, que vem de outra fonte.
  agenda: (q: string) => ['agenda', q] as const,
  anexos: (consulta: string) => ['anexos', consulta] as const,
  regras: (accountId: string) => ['regras', accountId] as const,
  // Sob o mesmo prefixo da conta: "Testar de novo" invalida as duas metades.
  ausencia: (accountId: string) => ['filtro-da-caixa', accountId, 'ausencia'] as const,
  encaminhamentoDaCaixa: (accountId: string) =>
    ['filtro-da-caixa', accountId, 'encaminhamento'] as const,
  filtroDaCaixa: (accountId: string) => ['filtro-da-caixa', accountId] as const,
  preferencias: ['preferencias'] as const,
  // Todas as caixas de uma vez: o composer troca de caixa sem nova ida ao servidor.
  assinaturas: ['assinaturas'] as const,
  respostasProntas: ['respostas-prontas'] as const,
  buscasSalvas: ['buscas-salvas'] as const,
};

export interface FiltrosDeConversa {
  accountId?: string | undefined;
  folderId?: string | undefined;
  filter: 'all' | 'unread' | 'flagged' | 'attachment' | 'snoozed';
  q?: string | undefined;
}

export function useContas() {
  return useQuery({
    queryKey: chaves.contas,
    queryFn: () => api<Conta[]>('/accounts'),
  });
}

export function usePastas(accountId: string | undefined) {
  return useQuery({
    queryKey: chaves.pastas(accountId ?? ''),
    queryFn: () => api<Pasta[]>(`/accounts/${accountId!}/folders`),
    enabled: accountId !== undefined,
  });
}

/** Rolagem infinita da Seção 6.4, paginada por cursor. */
export function useConversas(filtros: FiltrosDeConversa) {
  return useInfiniteQuery({
    queryKey: chaves.conversas(filtros),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ filter: filtros.filter, limit: '50' });
      if (filtros.accountId !== undefined) params.set('accountId', filtros.accountId);
      if (filtros.folderId !== undefined) params.set('folderId', filtros.folderId);
      if (filtros.q !== undefined && filtros.q.trim() !== '') params.set('q', filtros.q.trim());
      if (pageParam !== null) params.set('cursor', pageParam);

      return api<PaginaDeConversas>(`/threads?${params.toString()}`);
    },
    getNextPageParam: (ultima) => ultima.nextCursor,
    /**
     * Com o fluxo de eventos aberto (ADR 0053), quem avisa que a caixa mudou e
     * o servidor, e o polling vira rede de seguranca de 5 min. Sem fluxo (rede
     * que corta SSE, servidor antigo, aba que passou do limite), volta aos
     * 30 s do ADR 0017. Funcao, e nao valor: o React Query reavalia a cada ida.
     */
    refetchInterval: () => (tempoRealConectado() ? 5 * 60_000 : 30_000),
  });
}

export function conversasDaPagina(paginas: PaginaDeConversas[] | undefined): ConversaDaApi[] {
  return paginas?.flatMap((p) => p.items) ?? [];
}

/**
 * Busca do `GET /search` (Seção 9), sobre todas as pastas. Curta demais não vai
 * ao servidor: duas letras casam com meia caixa e a resposta não ajuda ninguém.
 */
export function useBusca(q: string, accountId: string | undefined) {
  const termo = q.trim();

  return useQuery({
    queryKey: chaves.busca(termo, accountId),
    queryFn: () => {
      const params = new URLSearchParams({ q: termo, limit: '30' });
      if (accountId !== undefined) params.set('accountId', accountId);
      return api<PaginaDeConversas>(`/search?${params.toString()}`);
    },
    enabled: termo.length >= 3,
    // O resultado anterior fica na tela enquanto o novo vem: sem piscar a lista.
    placeholderData: (anterior) => anterior,
  });
}

export function useMensagem(messageId: string | null) {
  return useQuery({
    queryKey: chaves.mensagem(messageId ?? ''),
    queryFn: () => api<MensagemDaApi>(`/messages/${messageId!}`),
    enabled: messageId !== null,
  });
}

export function useCorpo(messageId: string | null, carregarImagens: boolean) {
  return useQuery({
    queryKey: chaves.corpo(messageId ?? '', carregarImagens),
    queryFn: () =>
      api<CorpoDaApi>(
        `/messages/${messageId!}/body?images=${carregarImagens ? 'allowed' : 'blocked'}`,
      ),
    enabled: messageId !== null,
    // Resposta da API com erro ja diz o que houve (servidor de e-mail sem
    // resposta, mensagem fora da pasta); repetir so dobraria a espera. Quem
    // tenta de novo e o botao do aviso (ADR 0065).
    retry: (tentativas, erro) => !(erro instanceof ErroDaApi) && tentativas < 1,
  });
}

export type AcaoEmMassa =
  'read' | 'unread' | 'flag' | 'unflag' | 'archive' | 'delete' | 'spam' | 'move';

export function useAcaoEmMassa() {
  return useMutation<ResultadoDaAcao, Error, VarsAcao>({ mutationKey: FILA.acao });
}

/** Cancela arquivar/excluir/spam/mover na janela de Desfazer (ADR 0054). */
export function useDesfazer() {
  return useMutation<ResultadoDoDesfazer, Error, VarsDesfazer>({ mutationKey: FILA.desfazer });
}

/**
 * Sugestão de destinatário, vinda de quem você realmente troca e-mail.
 * Antes esta lista era inventada: o composer sugeria pessoas que nao existiam,
 * e dava para escolher uma e enviar para um endereco fabricado.
 */
export function useContatos(q: string) {
  const termo = q.trim();

  return useQuery({
    queryKey: chaves.contatos(termo),
    queryFn: () => api<Contato[]>(`/contacts/suggest?q=${encodeURIComponent(termo)}`),
    enabled: termo.length >= 2,
    // Contato não muda de minuto a minuto; segurar o resultado evita uma
    // requisição por tecla digitada.
    staleTime: 5 * 60_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Adiar (Seção 9). `until` nulo cancela o adiamento. */
export function useAdiar() {
  return useMutation<{ snoozedUntil: string | null }, Error, VarsAdiar>({
    mutationKey: FILA.adiar,
  });
}

// Fila de ações (Seção 11) ---------------------------------------------------

/**
 * As mutações que podem acontecer offline vivem numa fila.
 *
 * Por que aqui e não em cada tela: a fila precisa sobreviver a recarregar a
 * página sem rede. Uma mutação restaurada do disco não tem componente montado
 * nem closure - só a chave. Tudo que ela precisa para rodar sozinha depois
 * (o que chamar, o que pintar na hora, o que recarregar no fim) mora no padrão
 * registrado por `registrarFila`, e cada tela só informa a chave.
 *
 * O `scope` comum faz a fila drenar em ordem, uma de cada vez: arquivar e
 * depois desarquivar a mesma mensagem tem que chegar ao IMAP nessa ordem.
 */
export const FILA = {
  acao: ['fila', 'acao'] as const,
  adiar: ['fila', 'adiar'] as const,
  desfazer: ['fila', 'desfazer'] as const,
  enviar: ['fila', 'enviar'] as const,
};

export interface VarsAcao {
  ids: string[];
  action: AcaoEmMassa;
  folderId?: string;
}

export interface ResultadoDaAcao {
  afetadas: number;
  enfileiradas: number;
  /** Fichas das operacoes que ainda podem ser canceladas; vazio nas marcas. */
  desfazer?: string[];
}

export interface VarsDesfazer {
  ops: string[];
}

export interface ResultadoDoDesfazer {
  desfeitas: number;
  expiradas: number;
}

export interface VarsAdiar {
  messageId: string;
  until: string | null;
}

export interface Endereco {
  name: string;
  address: string;
}

export interface VarsEnvio {
  accountId: string;
  to: Endereco[];
  cc: Endereco[];
  bcc: Endereco[];
  subject: string;
  /** Um dos dois: sem `html`, a mensagem sai so em texto puro (ADR 0044). */
  html?: string;
  text?: string;
  attachments: (
    | { filename: string; contentType: string; content: string }
    | { deMensagem: string; anexoId: string }
    | { doRascunho: string }
  )[];
  inReplyTo?: string;
  references: string[];
  delaySeconds: number;
  /** Envio agendado (ISO): vale no lugar do atraso. */
  sendAt?: string;
  /**
   * Chave do envio (ADR 0052): a mesma chave nunca gera duas mensagens, nem
   * quando a fila reenvia depois de uma resposta perdida. Vai na fila junto,
   * porque o corpo e persistido com ela.
   */
  idempotencyKey?: string;
  /** Rascunho do servidor que sai da lista junto com o enfileiramento. */
  rascunhoId?: string;
}

type ListaDeConversas = InfiniteData<PaginaDeConversas, string | null>;

/**
 * Aplica a mudança em todas as listas já carregadas. Devolver `null` tira a
 * conversa da lista.
 */
function pintarConversas(
  queryClient: QueryClient,
  fn: (conversa: ConversaDaApi) => ConversaDaApi | null,
): void {
  queryClient.setQueriesData<ListaDeConversas>({ queryKey: ['conversas'] }, (dados) =>
    dados === undefined
      ? dados
      : {
          ...dados,
          pages: dados.pages.map((pagina) => ({
            ...pagina,
            items: pagina.items.map(fn).filter((c) => c !== null),
          })),
        },
  );
}

/**
 * Rede e 5xx passam; 4xx e resposta definitiva do servidor ("a caixa nao tem
 * pasta de spam"): repetir so atrasa o aviso em segundos, e o resultado e o mesmo.
 */
function repetirSoSePassageiro(tentativas: number, erro: Error): boolean {
  const definitivo =
    erro instanceof ErroDaApi &&
    erro.status >= 400 &&
    erro.status < 500 &&
    ![408, 429].includes(erro.status);
  if (definitivo) return false;
  return tentativas < 3;
}

export function registrarFila(queryClient: QueryClient): void {
  // Uma ação que falhou de vez deixou a tela mentindo: recarregar a lista traz
  // de volta a verdade do servidor, que é quem manda (Seção 10.4).
  const recarregar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['conversas'] });
    await queryClient.invalidateQueries({ queryKey: ['pastas'] });
  };

  queryClient.setMutationDefaults(FILA.acao, {
    scope: { id: 'fila' },
    retry: repetirSoSePassageiro,
    mutationFn: (vars: VarsAcao) =>
      api<ResultadoDaAcao>('/messages/batch', { method: 'POST', body: vars }),
    onMutate: async (vars: VarsAcao) => {
      // Sem isto, uma resposta em voo pinta por cima do que o usuário acabou de fazer.
      await queryClient.cancelQueries({ queryKey: ['conversas'] });

      const alvo = new Set(vars.ids);
      pintarConversas(queryClient, (conversa) => {
        if (!alvo.has(conversa.messageId)) return conversa;
        switch (vars.action) {
          case 'read':
            return { ...conversa, unread: false };
          case 'unread':
            return { ...conversa, unread: true };
          case 'flag':
            return { ...conversa, flagged: true };
          case 'unflag':
            return { ...conversa, flagged: false };
          default:
            // Arquivar, excluir, spam e mover: a mensagem sai desta lista.
            return null;
        }
      });
    },
    onSettled: recarregar,
  });

  // Na mesma fila (mesmo scope): o desfazer chega depois da acao que cancela,
  // mesmo com a rede indo e voltando entre as duas.
  queryClient.setMutationDefaults(FILA.desfazer, {
    scope: { id: 'fila' },
    retry: repetirSoSePassageiro,
    mutationFn: (vars: VarsDesfazer) =>
      api<ResultadoDoDesfazer>('/messages/undo', { method: 'POST', body: vars }),
    onSettled: recarregar,
  });

  queryClient.setMutationDefaults(FILA.adiar, {
    scope: { id: 'fila' },
    retry: repetirSoSePassageiro,
    mutationFn: (vars: VarsAdiar) =>
      api<{ snoozedUntil: string | null }>(`/messages/${vars.messageId}/snooze`, {
        method: 'POST',
        body: { until: vars.until },
      }),
    onMutate: async (vars: VarsAdiar) => {
      if (vars.until === null) return;
      await queryClient.cancelQueries({ queryKey: ['conversas'] });
      pintarConversas(queryClient, (c) => (c.messageId === vars.messageId ? null : c));
    },
    onSettled: recarregar,
  });

  queryClient.setMutationDefaults(FILA.enviar, {
    scope: { id: 'fila' },
    retry: repetirSoSePassageiro,
    mutationFn: (vars: VarsEnvio) =>
      api<{ id: string }>('/outbound', { method: 'POST', body: vars }),
  });
}

/**
 * Põe uma mensagem na fila de envio sem depender de componente montado: o
 * composer fecha no instante em que a pessoa clica em Enviar.
 */
export function enfileirarEnvio(
  queryClient: QueryClient,
  vars: VarsEnvio,
): Promise<{ id: string }> {
  const opcoes: MutationOptions<{ id: string }, Error, VarsEnvio> =
    queryClient.defaultMutationOptions({ mutationKey: FILA.enviar });
  return queryClient.getMutationCache().build(queryClient, opcoes).execute(vars);
}

export function useSincronizar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => api(`/accounts/${accountId}/sync`, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
}

// Agenda e regras (ADR 0042) -------------------------------------------------

/**
 * Fora da fila offline de proposito: editar contato e regra e configuracao,
 * nao triagem. Sem rede o botao falha e diz, em vez de prometer uma mudanca
 * que talvez colida com a de outro aparelho.
 */

const POR_PAGINA = 50;

/** Agenda paginada por deslocamento: a lista e alfabetica, nao cronologica. */
export function useAgenda(q: string) {
  const termo = q.trim();
  return useInfiniteQuery({
    queryKey: chaves.agenda(termo),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(POR_PAGINA), offset: String(pageParam) });
      if (termo !== '') params.set('q', termo);
      return api<PaginaDeContatos>(`/contacts?${params.toString()}`);
    },
    getNextPageParam: (ultima, todas) => {
      const lidos = todas.reduce((n, p) => n + p.items.length, 0);
      return lidos < ultima.total ? lidos : undefined;
    },
    placeholderData: (anterior) => anterior,
  });
}

/** Central de anexos (WYM-13): paginada por cursor, ate o fim da caixa. */
export function useAnexos(consulta: string) {
  return useInfiniteQuery({
    queryKey: chaves.anexos(consulta),
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams(consulta);
      if (pageParam !== '') params.set('cursor', pageParam);
      return api<PaginaDeAnexos>(`/attachments?${params.toString()}`);
    },
    getNextPageParam: (ultima) => ultima.nextCursor ?? undefined,
    placeholderData: (anterior) => anterior,
  });
}

export type ContatoEditavel = Omit<ContatoSalvo, 'id' | 'source' | 'updatedAt'>;

export function useSalvarContato() {
  const queryClient = useQueryClient();
  return useMutation<ContatoSalvo, Error, { id: string | null; dados: ContatoEditavel }>({
    mutationFn: ({ id, dados }) =>
      id === null
        ? api<ContatoSalvo>('/contacts', { method: 'POST', body: dados })
        : api<ContatoSalvo>(`/contacts/${id}`, { method: 'PATCH', body: dados }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agenda'] }),
  });
}

export function useApagarContato() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api<void>(`/contacts/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agenda'] }),
  });
}

/** O arquivo sobe em base64: o charset e decidido no servidor, nos bytes. */
export function useImportarContatos() {
  const queryClient = useQueryClient();
  return useMutation<ResultadoDaImportacao, Error, File>({
    mutationFn: async (arquivo) => {
      const bytes = new Uint8Array(await arquivo.arrayBuffer());
      let binario = '';
      // Em blocos: `String.fromCharCode(...bytes)` estoura a pilha em arquivo grande.
      for (let i = 0; i < bytes.length; i += 0x8000) {
        binario += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      }
      return api<ResultadoDaImportacao>('/contacts/import', {
        method: 'POST',
        body: { content: btoa(binario) },
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agenda'] }),
  });
}

export function useRegras(accountId: string | undefined) {
  return useQuery({
    queryKey: chaves.regras(accountId ?? ''),
    queryFn: () => api<RegraDaApi[]>(`/rules?accountId=${accountId!}`),
    enabled: accountId !== undefined,
  });
}

export type RegraEditavel = Omit<RegraDaApi, 'id' | 'priority'> & { priority?: number };

export function useSalvarRegra() {
  const queryClient = useQueryClient();
  return useMutation<RegraDaApi, Error, { id: string | null; dados: Partial<RegraEditavel> }>({
    mutationFn: ({ id, dados }) =>
      id === null
        ? api<RegraDaApi>('/rules', { method: 'POST', body: dados })
        : api<RegraDaApi>(`/rules/${id}`, { method: 'PATCH', body: dados }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['regras'] }),
  });
}

export function useApagarRegra() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api<void>(`/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['regras'] }),
  });
}

/** "Aplicar agora na Entrada". A lista muda quando o servidor confirmar. */
export function useAplicarRegras() {
  const queryClient = useQueryClient();
  return useMutation<ResultadoDaAcao & { avaliadas: number }, Error, string>({
    mutationFn: (accountId) => api('/rules/apply', { method: 'POST', body: { accountId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['conversas'] });
      await queryClient.invalidateQueries({ queryKey: ['pastas'] });
    },
  });
}

// Preferencias -----------------------------------------------------------------

export function usePreferencias() {
  return useQuery({
    queryKey: chaves.preferencias,
    queryFn: () => api<Preferencias>('/settings'),
    staleTime: 5 * 60_000,
  });
}

/** Parcial por secao: o servidor junta com o que ja estava salvo. */
export type MudancaDePreferencias = {
  [S in keyof Preferencias]?: Partial<Preferencias[S]>;
};

export function useSalvarPreferencias() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mudanca: MudancaDePreferencias) =>
      api<Preferencias>('/settings', { method: 'PATCH', body: mudanca }),
    onSuccess: (salvas) => queryClient.setQueryData(chaves.preferencias, salvas),
  });
}

// Identidades e respostas prontas (ADR 0044) -----------------------------------

/** Nome de exibicao da caixa: o que aparece no `From` de quem recebe. */
export function useSalvarIdentidade() {
  const queryClient = useQueryClient();
  return useMutation<Conta, Error, { accountId: string; displayName: string }>({
    mutationFn: ({ accountId, displayName }) =>
      api<Conta>(`/accounts/${accountId}`, { method: 'PATCH', body: { displayName } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaves.contas }),
  });
}

/** Foto da caixa (data URL); `null` remove. */
export function useSalvarFoto() {
  const queryClient = useQueryClient();
  return useMutation<Conta, Error, { accountId: string; photo: string | null }>({
    mutationFn: ({ accountId, photo }) =>
      api<Conta>(`/accounts/${accountId}/photo`, { method: 'PUT', body: { photo } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaves.contas }),
  });
}

export function useAssinaturas() {
  return useQuery({
    queryKey: chaves.assinaturas,
    queryFn: () => api<AssinaturaDaApi[]>('/signatures'),
    staleTime: 5 * 60_000,
  });
}

export type AssinaturaEditavel = Omit<AssinaturaDaApi, 'id'>;

export function useSalvarAssinatura() {
  const queryClient = useQueryClient();
  return useMutation<
    AssinaturaDaApi,
    Error,
    { id: string | null; dados: Partial<AssinaturaEditavel> }
  >({
    mutationFn: ({ id, dados }) =>
      id === null
        ? api<AssinaturaDaApi>('/signatures', { method: 'POST', body: dados })
        : api<AssinaturaDaApi>(`/signatures/${id}`, { method: 'PATCH', body: dados }),
    // Marcar uma como padrao desmarca outra no servidor: relê a lista inteira.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaves.assinaturas }),
  });
}

export function useApagarAssinatura() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api<void>(`/signatures/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaves.assinaturas }),
  });
}

export function useRespostasProntas() {
  return useQuery({
    queryKey: chaves.respostasProntas,
    queryFn: () => api<RespostaProntaDaApi[]>('/templates'),
    staleTime: 5 * 60_000,
  });
}

export function useSalvarRespostaPronta() {
  const queryClient = useQueryClient();
  return useMutation<
    RespostaProntaDaApi,
    Error,
    { id: string | null; dados: Omit<RespostaProntaDaApi, 'id'> }
  >({
    mutationFn: ({ id, dados }) =>
      id === null
        ? api<RespostaProntaDaApi>('/templates', { method: 'POST', body: dados })
        : api<RespostaProntaDaApi>(`/templates/${id}`, { method: 'PATCH', body: dados }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaves.respostasProntas }),
  });
}

export function useApagarRespostaPronta() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api<void>(`/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaves.respostasProntas }),
  });
}

// Buscas salvas (ADR 0050) -----------------------------------------------------

export function useBuscasSalvas() {
  return useQuery({
    queryKey: chaves.buscasSalvas,
    queryFn: () => api<BuscaSalvaDaApi[]>('/saved-searches'),
    staleTime: 5 * 60_000,
  });
}

export function useSalvarBusca() {
  const queryClient = useQueryClient();
  return useMutation<
    BuscaSalvaDaApi,
    Error,
    {
      id: string | null;
      dados: { name?: string; query?: string; accountId?: string | null; smart?: boolean };
    }
  >({
    mutationFn: ({ id, dados }) =>
      id === null
        ? api<BuscaSalvaDaApi>('/saved-searches', { method: 'POST', body: dados })
        : api<BuscaSalvaDaApi>(`/saved-searches/${id}`, { method: 'PATCH', body: dados }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaves.buscasSalvas }),
  });
}

export function useExcluirBusca() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api<void>(`/saved-searches/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaves.buscasSalvas }),
  });
}

// Gestao de pastas (ADR 0045) --------------------------------------------------

/*
 * Fora da fila offline, como contato e regra: so o servidor sabe se o nome
 * vale, e a tela precisa da resposta dele - nao de uma pasta que talvez nunca
 * exista. Sem rede o botao falha e diz.
 */

export type PapelDeSistema = 'sent' | 'drafts' | 'trash' | 'junk' | 'archive';

function useMudancaDePasta<V, R>(fn: (vars: V) => Promise<R>, tambemConversas = false) {
  const queryClient = useQueryClient();
  return useMutation<R, Error, V>({
    mutationFn: fn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pastas'] });
      if (tambemConversas) await queryClient.invalidateQueries({ queryKey: ['conversas'] });
    },
  });
}

export function useCriarPasta(accountId: string) {
  return useMudancaDePasta((vars: { name: string; parentId: string | null }) =>
    api<Pasta>(`/accounts/${accountId}/folders`, { method: 'POST', body: vars }),
  );
}

export interface MudancaDePasta {
  id: string;
  name?: string;
  /** `null` leva para a raiz. */
  parentId?: string | null;
  subscribed?: boolean;
}

/** Renomear, mover ou assinar. */
export function useMudarPasta(accountId: string) {
  return useMudancaDePasta(({ id, ...mudanca }: MudancaDePasta) =>
    api<Pasta>(`/accounts/${accountId}/folders/${id}`, { method: 'PATCH', body: mudanca }),
  );
}

export function useExcluirPasta(accountId: string) {
  // As mensagens da pasta somem junto: a lista aberta nela tambem.
  return useMudancaDePasta(
    (id: string) => api<void>(`/accounts/${accountId}/folders/${id}`, { method: 'DELETE' }),
    true,
  );
}

/** `folderId` nulo volta para a pasta que o servidor indica. */
export function useEscolherPapel(accountId: string) {
  return useMudancaDePasta((vars: { role: PapelDeSistema; folderId: string | null }) =>
    api<void>(`/accounts/${accountId}/folder-roles/${vars.role}`, {
      method: 'PUT',
      body: { folderId: vars.folderId },
    }),
  );
}
